import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import {
    TabsMenu,
    TabTrigger
} from "@solomon-apps/shared/src/components/ui/tabs-menu";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { motion } from "motion/react";
import React, { useState } from "react";

import {
    CurrencyCappedInput,
    CurrencyDelegatedInput,
    DaysSliderInput,
    OptionsEditor,
    PercentageSplitInput,
    RangeAxisInput,
    TierSegmentedInput
} from "./ProgramPolicies/components/PolicyValueEditor";
import type { OptionPolicyConstraint } from "./ProgramPolicies/components/PolicyValueEditor";
import {
    CurrencyCappedProposal,
    CurrencyDelegatedProposal,
    DaysSliderProposal,
    PercentageSplitProposal,
    RangeAxisProposal,
    SaveErrorProposal,
    SavingStateProposal,
    TierSegmentedProposal,
    ValidationErrorProposal
} from "./ProgramPolicies/components/PolicyValueEditorProposals";

type ExplorationTab = "current" | "proposed";

const OPTIONS_CONSTRAINT: OptionPolicyConstraint = {
    type: "options",
    options: [
        { value: "enabled", label: "Enabled — accounts may hold and transact" },
        { value: "disabled", label: "Disabled — accounts are restricted" },
        {
            value: "pending-review",
            label: "Pending review — subject to manual approval"
        }
    ]
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ExplorationPoliciesPage(): React.ReactElement {
    const [tab, setTab] = useState<ExplorationTab>("current");

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex w-full flex-col"
        >
            <header className="border-b border-line-base px-6 py-6">
                <Text variant="label" className="text-foreground-muted">
                    Exploration
                </Text>
                <Text variant="h1" className="mt-2 text-foreground-base">
                    Policy — Set Value
                </Text>
                <Text
                    variant="small"
                    className="mt-3 max-w-xl text-foreground-muted"
                >
                    Side-by-side comparison: current production variants vs.
                    proposed editor sketches.
                </Text>
                <div className="mt-5">
                    <TabsMenu
                        value={tab}
                        onValueChange={(value) =>
                            setTab(value as ExplorationTab)
                        }
                        align="start"
                        border="bottom"
                    >
                        <TabTrigger value="current" size="sm">
                            Current
                        </TabTrigger>
                        <TabTrigger value="proposed" size="sm">
                            Proposed
                        </TabTrigger>
                    </TabsMenu>
                </div>
            </header>

            {tab === "current" ? <CurrentVariants /> : <ProposedVariants />}
        </motion.div>
    );
}

function CurrentVariants(): React.ReactElement {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-line-base">
            <div className="divide-y divide-line-base">
                <Variant title="Numeric / Currency / Delegated">
                    <NumericCurrencyDelegated />
                </Variant>
                <Variant title="Numeric / Currency / Constrained">
                    <NumericCurrencyConstrained />
                </Variant>
                <Variant title="Numeric / Percentage — fee-split">
                    <NumericPercentage />
                </Variant>
                <Variant title="Numeric / Days — min holding period">
                    <NumericDays />
                </Variant>
                <Variant title="Numeric / Tier — KYC tier">
                    <NumericTier />
                </Variant>
            </div>
            <div className="divide-y divide-line-base">
                <Variant title="Range / Currency — floor + ceiling">
                    <RangeVariant />
                </Variant>
                <Variant title="Options / Enum select">
                    <OptionsVariant />
                </Variant>
                <Variant title="State / Saving">
                    <SavingVariant />
                </Variant>
                <Variant title="State / Validation error">
                    <ValidationErrorVariant />
                </Variant>
                <Variant title="State / Save error (API)">
                    <SaveErrorVariant />
                </Variant>
            </div>
        </div>
    );
}

function ProposedVariants(): React.ReactElement {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-line-base">
            <div className="divide-y divide-line-base">
                <Variant
                    title="Tier — segmented control"
                    subtitle="3 discrete values, zero invalid input possible"
                >
                    <TierSegmentedProposal />
                </Variant>
                <Variant
                    title="Days — slider + paired numeric"
                    subtitle="drag for ballpark, type for precision, hard-capped at issuer limit"
                >
                    <DaysSliderProposal />
                </Variant>
                <Variant
                    title="Percentage — divided bar"
                    subtitle="the bar IS the split; you vs. issuer is tangible"
                >
                    <PercentageSplitProposal />
                </Variant>
                <Variant
                    title="Currency capped — slider + quick-set chips"
                    subtitle="cap is a hard edge on the track, not a sentence"
                >
                    <CurrencyCappedProposal />
                </Variant>
                <Variant
                    title="Currency delegated — magnitude preview"
                    subtitle="no cap to anchor against; catches 10× fat-finger mistakes"
                >
                    <CurrencyDelegatedProposal />
                </Variant>
            </div>
            <div className="divide-y divide-line-base">
                <Variant
                    title="Range — single axis with rails"
                    subtitle="floor & ceiling as labeled stops; thumb position = value at a glance"
                >
                    <RangeAxisProposal />
                </Variant>
                <Variant
                    title="State / Saving — locked + inline spinner"
                    subtitle="commit is visible inside the value, not just on the button"
                >
                    <SavingStateProposal />
                </Variant>
                <Variant
                    title="State / Validation — inline error under field"
                    subtitle="error sits next to the typo, not in the action row"
                >
                    <ValidationErrorProposal />
                </Variant>
                <Variant
                    title="State / Save error — persistent banner + retry"
                    subtitle="failed network call should be promoted, not fine-print"
                >
                    <SaveErrorProposal />
                </Variant>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function Variant({
    title,
    subtitle,
    children
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <section>
            <div className="border-b border-line-base px-6 py-3">
                <Text variant="label" className="text-foreground-neutral">
                    {title}
                </Text>
                {subtitle ? (
                    <Text
                        variant="label"
                        className="mt-1 text-foreground-neutral normal-case"
                    >
                        {subtitle}
                    </Text>
                ) : null}
            </div>
            <div className="px-6 py-6">{children}</div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// SetValueShell — mirrors the PolicyEditor container
// ---------------------------------------------------------------------------

interface SetValueShellProps {
    helper: string;
    draft: string;
    saving?: boolean;
    canSave?: boolean;
    validationError?: string | null;
    saveError?: string | null;
    onSave?: () => void;
    children: React.ReactNode;
}

function SetValueShell({
    helper,
    draft,
    saving = false,
    canSave,
    validationError,
    saveError,
    onSave,
    children
}: SetValueShellProps): React.ReactElement {
    const derivedCanSave = canSave ?? (!validationError && !saving);

    return (
        <Card surface="sunken" rounded={false} className="p-6">
            <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <Text variant="label" className="text-foreground-muted">
                    Set value
                </Text>
                <Text variant="fine-print" className="text-foreground-muted">
                    {helper}
                </Text>
            </div>

            {children}

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button onClick={onSave} disabled={!derivedCanSave}>
                    {saving ? "Saving..." : "Save value"}
                </Button>
                {validationError && draft.trim() ? (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-error-base"
                    >
                        {validationError}
                    </Text>
                ) : null}
                {saveError ? (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-error-base"
                    >
                        {saveError}
                    </Text>
                ) : null}
            </div>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

function NumericCurrencyDelegated(): React.ReactElement {
    const [draft, setDraft] = useState("1001");
    return (
        <SetValueShell helper="No issuer limit on this rule" draft={draft}>
            <CurrencyDelegatedInput
                draft={draft}
                onDraftChange={setDraft}
                unit=""
            />
        </SetValueShell>
    );
}

function NumericCurrencyConstrained(): React.ReactElement {
    const [draft, setDraft] = useState("2500");
    return (
        <SetValueShell helper="Issuer allows 5,000" draft={draft}>
            <CurrencyCappedInput
                draft={draft}
                onDraftChange={setDraft}
                cap={5000}
                unit=""
            />
        </SetValueShell>
    );
}

function NumericPercentage(): React.ReactElement {
    const [draft, setDraft] = useState("15");
    return (
        <SetValueShell helper="No issuer limit on this rule" draft={draft}>
            <PercentageSplitInput draft={draft} onDraftChange={setDraft} />
        </SetValueShell>
    );
}

function NumericDays(): React.ReactElement {
    const [draft, setDraft] = useState("30");
    return (
        <SetValueShell helper="Issuer allows up to 90 days" draft={draft}>
            <DaysSliderInput draft={draft} onDraftChange={setDraft} cap={90} />
        </SetValueShell>
    );
}

function NumericTier(): React.ReactElement {
    const [draft, setDraft] = useState("2");
    return (
        <SetValueShell helper="Issuer allows tier 1–3" draft={draft}>
            <TierSegmentedInput draft={draft} onDraftChange={setDraft} />
        </SetValueShell>
    );
}

function RangeVariant(): React.ReactElement {
    const [draft, setDraft] = useState("5000");
    return (
        <SetValueShell helper="Issuer allows 0–10,000" draft={draft}>
            <RangeAxisInput
                draft={draft}
                onDraftChange={setDraft}
                floor={0}
                ceiling={10000}
                unit=""
            />
        </SetValueShell>
    );
}

function OptionsVariant(): React.ReactElement {
    const [draft, setDraft] = useState("enabled");
    return (
        <SetValueShell helper="No issuer limit on this rule" draft={draft}>
            <OptionsEditor
                constraint={OPTIONS_CONSTRAINT}
                draft={draft}
                onDraftChange={setDraft}
            />
        </SetValueShell>
    );
}

function SavingVariant(): React.ReactElement {
    return (
        <SetValueShell
            helper="No issuer limit on this rule"
            draft="1001"
            saving
            canSave={false}
        >
            <CurrencyDelegatedInput
                draft="1001"
                onDraftChange={() => undefined}
                unit=""
            />
        </SetValueShell>
    );
}

function ValidationErrorVariant(): React.ReactElement {
    const [draft, setDraft] = useState("999999999");
    const error =
        Number(draft) > 10000
            ? "Value exceeds the maximum allowed amount"
            : null;
    return (
        <SetValueShell
            helper="Issuer allows up to 10,000"
            draft={draft}
            validationError={error}
        >
            <CurrencyCappedInput
                draft={draft}
                onDraftChange={setDraft}
                cap={10000}
                unit=""
            />
        </SetValueShell>
    );
}

function SaveErrorVariant(): React.ReactElement {
    const [draft, setDraft] = useState("5000");
    return (
        <SetValueShell
            helper="No issuer limit on this rule"
            draft={draft}
            saveError="Request failed: policy scope unavailable for this Program Surface"
        >
            <CurrencyDelegatedInput
                draft={draft}
                onDraftChange={setDraft}
                unit=""
            />
        </SetValueShell>
    );
}
